import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/features/auth/AuthProvider";
import { useAccountStatus } from "@/features/security/hooks/useAccountStatus";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Guard for routes that require an authenticated, non-suspended account.
 *
 * Identity check is synchronous via the AuthProvider — no extra network
 * request. Suspension status is fetched lazily via useAccountStatus and
 * cached, so navigation between protected routes does not refetch.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { isSuspended, loading: statusLoading } = useAccountStatus();
  const location = useLocation();

  if (authLoading || (user && statusLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Carry the destination, so the auth page can bring the visitor back
    // here instead of to the Recap dashboard. Before this, a marketing link
    // into /originals signed someone up and dropped them in the wrong
    // studio. The dashboard is the auth page's default, so it needs no param.
    const next = `${location.pathname}${location.search}`;
    const to = next === "/dashboard" ? "/auth" : `/auth?next=${encodeURIComponent(next)}`;
    return <Navigate to={to} replace />;
  }

  if (isSuspended) {
    return <Navigate to="/account-suspended" replace />;
  }

  return <>{children}</>;
}
